/**
 * Admin `/admin` (role=superadmin) — the platform control panel.
 *
 * - platformSettings form (creation experience, defaultEngine, concurrency,
 *   quotas, quality, featured slugs, motd) via the settings repo.
 * - All-creations table (live) with unpublish/delete of ANY creation.
 * - Role manager: email → grant/revoke creator/superadmin (setUserRole callable).
 * - Build queue monitor: the newest build jobs, headed by how many are in flight.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MAX_ASSET_GO_LIVE_BUDGET_MS, MAX_FIRST_RELEASE_MEDIA_WAIT_MS, type Creation, type BuildJob, type PlatformSettings, type Role } from '@djgg/creations';
import { useServices } from '../services/context.js';
import { useSubscription } from '../hooks/useSubscription.js';
import { useAttributedAction } from '../hooks/useAttributedAction.js';
import { engineOptions } from '../studio/engineOptions.js';
import { BuildPipelinesPanel } from '../components/BuildPipelinesPanel.js';
import { LlmMonitor } from '../components/LlmMonitor.js';
import { ActionLog } from '../components/ActionLog.js';
import { Mascot } from '../components/Mascot.js';
import { formatTimeAgo } from '../components/relativeTime.js';
import { FailedCandidateInspectionPanel } from '../components/FailedCandidateInspectionPanel.js';
import { QualityV2Panel } from '../components/QualityV2Panel.js';

function SettingsForm() {
  const services = useServices();
  const options = engineOptions();
  const [settings, setSettings] = useState<PlatformSettings | null>(null);
  const [pilotCreationIds, setPilotCreationIds] = useState<string[]>([]);
  const [loadError, setLoadError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [builderReadiness, setBuilderReadiness] = useState<{
    reachable: boolean;
    music?: { ready: boolean; provider?: string; reason?: string };
    sfx?: { ready: boolean; provider?: string; reason?: string };
  } | null>(null);

  function load() {
    setLoadError('');
    setSettings(null);
    void services.callable.getPlatformSettings({}).then(({ settings: next, adminSettings, builderReadiness: readiness }) => {
      setSettings(next);
      setPilotCreationIds(adminSettings.mediaReviewPilotCreationIds);
      setBuilderReadiness(readiness ?? null);
    }).catch(() => {
      setLoadError('We couldn’t load the live platform settings. Nothing can be saved until you retry.');
    });
  }

  useEffect(load, [services]);

  async function save() {
    if (!settings) return;
    setBusy(true);
    setSaved(false);
    setSaveError('');
    try {
      const result = await services.callable.updatePlatformSettings({
        expectedRevision: settings.revision,
        settings,
        mediaReviewPilotCreationIds: pilotCreationIds,
      });
      setSettings(result.settings);
      setPilotCreationIds(result.adminSettings.mediaReviewPilotCreationIds);
      setSaved(true);
    } catch {
      setSaveError('The settings changed or could not be saved. Reload the live values and try again.');
    } finally {
      setBusy(false);
    }
  }

  if (loadError) {
    return (
      <div className="panel">
        <h2 className="panel__title">Platform settings</h2>
        <p role="alert">{loadError}</p>
        <button type="button" className="btn btn--primary" onClick={load}>Try again</button>
      </div>
    );
  }
  if (!settings) {
    return <div className="panel" role="status">Loading live platform settings…</div>;
  }

  return (
    <div className="panel">
      <h2 className="panel__title">Platform settings</h2>
      <p className="muted">Every new game uses the Deej-led complete creation flow. Historical build recipes remain available for saved work.</p>
      <div className="field">
        <label className="field__label">Default engine</label>
        <select
          className="select"
          value={settings.defaultEngine.model}
          onChange={(e) => {
            const opt = options.find((o) => o.value === e.target.value);
            if (opt) setSettings({ ...settings, defaultEngine: opt.engine });
          }}
        >
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>
      <p className="muted" style={{ marginTop: -4 }}>
        The autonomous quality loop runs only when its Advanced creator feature is enabled and
        max quality passes is above 1. Existing stored pass counts remain dormant until then.
      </p>
      <div className="row">
        <div className="field" style={{ flex: 1 }}>
          <label className="field__label">Build concurrency</label>
          <input
            className="input"
            type="number"
            min={1}
            value={settings.buildConcurrency}
            onChange={(e) =>
              setSettings({ ...settings, buildConcurrency: Number(e.target.value) })
            }
          />
        </div>
        <div className="field" style={{ flex: 1 }}>
          <label className="field__label">Max versions / creator / day</label>
          <input
            className="input"
            type="number"
            min={1}
            value={settings.maxVersionsPerDay}
            onChange={(e) =>
              setSettings({ ...settings, maxVersionsPerDay: Number(e.target.value) })
            }
          />
        </div>
        <div className="field" style={{ flex: 1 }}>
          <label className="field__label">Max asset gens / creator / day</label>
          <input
            className="input"
            type="number"
            min={1}
            value={settings.maxAssetGensPerDay}
            onChange={(e) =>
              setSettings({ ...settings, maxAssetGensPerDay: Number(e.target.value) })
            }
          />
        </div>
      </div>
      <div className="row">
        <div className="field" style={{ flex: 1 }}>
          <label className="field__label">Max quality passes / build</label>
          <input
            className="input"
            type="number"
            min={1}
            value={settings.maxQualityPasses}
            onChange={(e) =>
              setSettings({ ...settings, maxQualityPasses: Number(e.target.value) })
            }
          />
        </div>
        <div className="field" style={{ flex: 1 }}>
          <label className="field__label">Quality bar (0–1)</label>
          <input
            className="input"
            type="number"
            min={0}
            max={1}
            step={0.05}
            value={settings.qualityBar}
            onChange={(e) => setSettings({ ...settings, qualityBar: Number(e.target.value) })}
          />
        </div>
        <div className="field" style={{ flex: 1 }}>
          <label className="field__label">Asset go-live budget (ms)</label>
          <input
            className="input"
            type="number"
            min={0}
            max={MAX_ASSET_GO_LIVE_BUDGET_MS}
            step={10000}
            value={settings.assetGoLiveBudgetMs}
            onChange={(e) =>
              setSettings({ ...settings, assetGoLiveBudgetMs: Number(e.target.value) })
            }
          />
        </div>
        <div className="field" style={{ flex: 1 }}>
          <label className="field__label">First release media wait (ms)</label>
          <input
            className="input"
            type="number"
            min={0}
            max={MAX_FIRST_RELEASE_MEDIA_WAIT_MS}
            step={30000}
            value={settings.firstReleaseMediaWaitMs}
            onChange={(e) =>
              setSettings({ ...settings, firstReleaseMediaWaitMs: Number(e.target.value) })
            }
          />
        </div>
      </div>
      <fieldset className="field" style={{ border: 0, padding: 0 }}>
        <legend className="field__label">Advanced creator features</legend>
        <p className="muted">
          These owner controls take effect without a redeploy. Turning one off affects new work;
          it does not rewrite completed games.
        </p>
        <div className="row" style={{ flexWrap: 'wrap' }}>
          {([
            ['creationFilms', 'Creation films for new versions'],
            ['workshopSelections', 'Interactive workshop choices'],
            ['videoReferences', 'Reference-video discovery'],
            ['suggestionPass', 'Suggested next-change prompts'],
            ['diagnosisLoop', 'Diagnosis repair loop'],
            ['qualityLoop', 'Autonomous quality loop'],
            ['assetGoLiveBudget', 'Bounded asset go-live wait'],
            ['mediaReviewPilot', 'Visual media-review pilot'],
          ] as const).map(([key, label]) => (
            <label className="field" style={{ flex: '1 1 280px' }} key={key}>
              <input
                type="checkbox"
                checked={settings.creationFeatures[key]}
                onChange={(e) => setSettings({
                  ...settings,
                  creationFeatures: {
                    ...settings.creationFeatures,
                    [key]: e.target.checked,
                  },
                })}
              />{' '}
              {label}
            </label>
          ))}
        </div>
        <div className="field">
          <label className="field__label" htmlFor="media-review-pilot-creations">
            Visual media-review pilot creation IDs (comma-separated)
          </label>
          <input
            id="media-review-pilot-creations"
            className="input"
            value={pilotCreationIds.join(', ')}
            disabled={!settings.creationFeatures.mediaReviewPilot}
            onChange={(e) => setPilotCreationIds([...new Set(e.target.value
              .split(',')
              .map((id) => id.trim())
              .filter(Boolean))])}
          />
          <p className="muted">
            Visual review also requires a superadmin who belongs to the listed creation. Audio
            review remains available to ordinary creation members.
          </p>
        </div>
      </fieldset>
      <div className="field">
        <label className="field__label">Featured slugs (comma-separated)</label>
        <input
          className="input"
          value={settings.galleryFeaturedSlugs.join(', ')}
          onChange={(e) =>
            setSettings({
              ...settings,
              galleryFeaturedSlugs: e.target.value
                .split(',')
                .map((s) => s.trim())
                .filter(Boolean),
            })
          }
        />
      </div>
      <div className="field">
        <label className="field__label">Message of the day</label>
        <input
          className="input"
          value={settings.motd}
          onChange={(e) => setSettings({ ...settings, motd: e.target.value })}
        />
      </div>
      <div className="row">
        <label className="field" style={{ flex: 1 }}>
          <input
            type="checkbox"
            checked={settings.audioGeneration.music}
            onChange={(e) =>
              setSettings({
                ...settings,
                audioGeneration: { ...settings.audioGeneration, music: e.target.checked },
              })
            }
          />{' '}
          Provider-generated music
        </label>
        <label className="field" style={{ flex: 1 }}>
          <input
            type="checkbox"
            checked={settings.audioGeneration.sfx}
            onChange={(e) =>
              setSettings({
                ...settings,
                audioGeneration: { ...settings.audioGeneration, sfx: e.target.checked },
              })
            }
          />{' '}
          LLM-shaped, locally rendered sound effects
        </label>
      </div>
      <p className="muted" style={{ marginTop: -4 }}>
        Each generation is a paid model call. These Admin switches are the spend authority;
        the Builder also verifies model readiness before starting a job. Sound-effect audio is rendered locally from validated parameters.
      </p>
      <p className="muted" role="status">
        Builder readiness:{' '}
        {!builderReadiness
          ? 'not reported'
          : !builderReadiness.reachable
            ? 'unavailable — generation will fail closed'
            : `music ${builderReadiness.music?.ready ? `ready (${builderReadiness.music.provider ?? 'configured'})` : `not ready (${builderReadiness.music?.reason ?? 'unknown'})`}; sound effects ${builderReadiness.sfx?.ready ? `ready (${builderReadiness.sfx.provider ?? 'configured'})` : `not ready (${builderReadiness.sfx?.reason ?? 'unknown'})`}`}
      </p>
      <button className="btn btn--primary" onClick={() => void save()} disabled={busy}>
        {busy ? 'Saving…' : 'Save settings'}
      </button>
      {saved && <span className="muted" style={{ marginLeft: 10 }}>Saved.</span>}
      {saveError && (
        <div>
          <p role="alert">{saveError}</p>
          <button type="button" className="btn" onClick={load}>Reload settings</button>
        </div>
      )}
    </div>
  );
}

function EditorsPickPanel() {
  const services = useServices();
  const creations = useSubscription<Array<Creation & { id: string }> | null>(
    services.subscribe.creations,
    null,
    [],
  );
  const [pinned, setPinned] = useState('');
  const [excluded, setExcluded] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [lastRun, setLastRun] = useState<{ lastRunAt?: number; edition?: string }>({});

  useEffect(() => {
    services.repos.settings
      .get()
      .then((s) => {
        setPinned(s.editorsPick.pinned.join(', '));
        setExcluded(s.editorsPick.excluded.join(', '));
        setLastRun({ lastRunAt: s.editorsPick.lastRunAt, edition: s.editorsPick.edition });
      })
      .finally(() => setLoaded(true));
  }, [services]);

  const parse = (v: string): string[] =>
    v
      .split(',')
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean);

  // The active list, derived from the SAME creations subscription the gallery
  // uses (each pick carries its `editorsPick.current` mark), ordered by rank.
  const current = (creations ?? [])
    .filter((c) => c.editorsPick?.current)
    .sort((a, b) => (a.editorsPick?.currentRank ?? 999) - (b.editorsPick?.currentRank ?? 999));

  async function rePick() {
    setBusy(true);
    setMsg('');
    try {
      const res = await services.callable.pickEditorsPicks({
        pinned: parse(pinned),
        excluded: parse(excluded),
      });
      setMsg(
        `Edition ${res.edition}: ${res.picks.length} picks (reviewed ${res.reviewed}, reused ${res.reused}, ${res.awardedBadges} new badges).`,
      );
      setLastRun({ lastRunAt: Date.now(), edition: res.edition });
    } catch (err) {
      setMsg(err instanceof Error ? err.message : "Editors' Pick run failed.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="panel">
      <h2 className="panel__title">Editors' Pick</h2>
      <p className="muted" style={{ marginTop: -4 }}>
        The weekly LLM-reviewed shelf. Pins force a game in; excludes keep one out — both survive the
        cron.{' '}
        {lastRun.lastRunAt
          ? `Last run ${new Date(lastRun.lastRunAt).toLocaleString()} (${lastRun.edition}).`
          : 'Not run yet.'}
      </p>
      <div className="field">
        <label className="field__label">Current picks ({current.length})</label>
        {current.length ? (
          <ol style={{ margin: '4px 0 0', paddingLeft: 20 }} data-testid="admin-editors-pick-current">
            {current.map((c) => {
              const reviewedV = c.editorsPick?.reviewedVersion ?? 0;
              return (
                <li key={c.id}>
                  <Link to={`/g/${c.slug}`}>{c.title}</Link>{' '}
                  <span className="muted">@{c.creatorHandle}</span>
                  {reviewedV > 0 && (
                    <span className="muted">
                      {' '}
                      · reviewed v{reviewedV}
                      {c.activeVersion > reviewedV ? ` — live v${c.activeVersion} unreviewed` : ''}
                    </span>
                  )}
                </li>
              );
            })}
          </ol>
        ) : (
          <span className="muted">None yet.</span>
        )}
      </div>
      <div className="field">
        <label className="field__label">Pinned slugs (comma-separated)</label>
        <input
          className="input"
          value={pinned}
          onChange={(e) => setPinned(e.target.value)}
          disabled={!loaded}
          placeholder="force-include these games"
        />
      </div>
      <div className="field">
        <label className="field__label">Excluded slugs (comma-separated)</label>
        <input
          className="input"
          value={excluded}
          onChange={(e) => setExcluded(e.target.value)}
          disabled={!loaded}
          placeholder="never pick these games"
        />
      </div>
      <button className="btn btn--primary" onClick={() => void rePick()} disabled={busy || !loaded}>
        {busy ? 'Reviewing…' : 'Save & re-pick now'}
      </button>
      {msg && (
        <span className="muted" style={{ marginLeft: 10 }} data-testid="admin-editors-pick-msg">
          {msg}
        </span>
      )}
    </div>
  );
}

function RoleManager() {
  const services = useServices();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>('creator');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  async function apply(grant: boolean) {
    setMessage('');
    setBusy(true);
    try {
      const res = await services.callable.setUserRole({ email: email.trim(), role: grant ? role : null });
      setMessage(grant ? `Granted ${role} to ${email} (${res.uid}).` : `Revoked role from ${email}.`);
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Role change failed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="panel">
      <h2 className="panel__title">Roles</h2>
      <div className="row" style={{ alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div className="field" style={{ flex: 2, marginBottom: 0 }}>
          <label className="field__label">User email</label>
          <input
            className="input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="creator@example.com"
          />
        </div>
        <div className="field" style={{ marginBottom: 0 }}>
          <label className="field__label">Role</label>
          <select className="select" value={role} onChange={(e) => setRole(e.target.value as Role)}>
            <option value="creator">creator</option>
            <option value="superadmin">superadmin</option>
          </select>
        </div>
        <button className="btn btn--primary" onClick={() => void apply(true)} disabled={busy || !email.trim()}>
          Grant
        </button>
        <button className="btn btn--danger" onClick={() => void apply(false)} disabled={busy || !email.trim()}>
          Revoke
        </button>
      </div>
      {message && <p className="muted" style={{ marginTop: 12 }}>{message}</p>}
    </div>
  );
}

function AllCreations() {
  const services = useServices();
  // ALL creations (no visibility filter) — an unpublished creation must stay in
  // the admin table so a superadmin can re-publish or delete it. Using the
  // public `creations` subscription here would make unlisted rows vanish.
  const rows = useSubscription<Array<Creation & { id: string }>>(
    services.subscribe.allCreations,
    [],
    [],
  );
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState('');
  const { runAttributed, attributionModal } = useAttributedAction();

  // Both admin mutations go through the TRUSTED callables (not direct Firestore
  // writes) so they carry attribution and land in the audit log. The admin is
  // usually NOT a member here, so runAttributed opens the collaborator picker
  // (the allCreations rows carry the editors it lists).
  function setVisibility(c: Creation & { id: string }, visibility: 'public' | 'unlisted') {
    void runAttributed(c, async (attributeTo) => {
      setError('');
      setBusyId(c.id);
      try {
        await services.callable.updateCreation({ creationId: c.id, visibility, attributeTo });
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to update visibility.');
      } finally {
        setBusyId(null);
      }
    });
  }

  function remove(c: Creation & { id: string }) {
    void runAttributed(c, async (attributeTo) => {
      setError('');
      setBusyId(c.id);
      try {
        await services.callable.deleteCreation({ creationId: c.id, attributeTo });
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to delete creation.');
      } finally {
        setBusyId(null);
      }
    });
  }

  return (
    <div className="panel">
      <h2 className="panel__title">All creations ({rows.length})</h2>
      {error && <div className="error-text" style={{ marginBottom: 10 }}>{error}</div>}
      <div className="table__scroll">
        <table className="table">
          <thead>
            <tr>
              <th>Title</th>
              <th>Creator</th>
              <th>Status</th>
              <th>Visibility</th>
              <th>Created</th>
              <th>Updated</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <tr
                key={c.id}
                data-testid="admin-creation-row"
                data-slug={c.slug}
                data-visibility={c.visibility}
              >
                <td>{c.title}</td>
                <td>@{c.creatorHandle}</td>
                <td>{c.status}</td>
                <td>{c.visibility}</td>
                <td className="muted" title={new Date(c.createdAt).toLocaleString()}>
                  {formatTimeAgo(c.createdAt)}
                </td>
                <td className="muted" title={new Date(c.updatedAt).toLocaleString()}>
                  {formatTimeAgo(c.updatedAt)}
                </td>
                <td className="row">
                  {/* The game page opens every visibility for a superadmin
                      (RLS: drafts are member-or-admin readable), so this is
                      the one way to play an unlisted game or a draft fork. */}
                  <Link className="btn btn--sm" to={`/g/${c.slug}`} data-testid="admin-view-game">
                    View / Play
                  </Link>
                  {/* Draft = a pre-divergence fork: only the builder can exit
                      it (server guard + DB trigger), so offer no visibility
                      control — the old binary ternary rendered a doomed
                      "Unpublish" here (review finding). */}
                  {c.visibility === 'draft' ? (
                    <span className="pill" data-testid="admin-draft-pill" title="A fork stays private until its first divergence publishes it">
                      draft fork
                    </span>
                  ) : c.visibility === 'unlisted' ? (
                    <button
                      className="btn btn--sm"
                      disabled={busyId === c.id}
                      onClick={() => void setVisibility(c, 'public')}
                      data-testid="admin-publish"
                    >
                      Publish
                    </button>
                  ) : (
                    <button
                      className="btn btn--sm"
                      disabled={busyId === c.id}
                      onClick={() => void setVisibility(c, 'unlisted')}
                      data-testid="admin-unpublish"
                    >
                      Unpublish
                    </button>
                  )}
                  <button
                    className="btn btn--sm btn--danger"
                    disabled={busyId === c.id}
                    onClick={() => void remove(c)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {attributionModal}
    </div>
  );
}

/**
 * Short display slice of an id field that may be momentarily absent — a
 * realtime partial snapshot row can lack any column until the next full emit.
 */
function shortId(v: string | null | undefined, n: number): string {
  return v ? v.slice(0, n) : '—';
}

/** Coarse age formatter for the build-queue monitor (ms → "3m 12s"). */
function fmtAge(ms: number): string {
  if (ms <= 0) return '0s';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  return m < 60 ? `${m}m ${s % 60}s` : `${Math.floor(m / 60)}h ${m % 60}m`;
}

function BuildQueue() {
  const services = useServices();
  const [error, setError] = useState<string | null>(null);
  const [actionErr, setActionErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const jobs = useSubscription<Array<BuildJob & { id: string }>>(
    (cb, onError) =>
      services.subscribe.buildJobs((next) => {
        setError(null);
        cb(next);
      }, onError),
    [],
    [],
    {
      // The buildJobs collection is trusted-writer territory; a client listener
      // may be denied by rules. Surface it here instead of an uncaught error.
      onError: (err) =>
        setError(err instanceof Error ? err.message : 'Build queue is not readable.'),
    },
  );
  // Fixed per render — the age is a monitor snapshot, refreshed as jobs stream in.
  const now = Date.now();
  const inFlight = jobs.filter((j) => j.status === 'queued' || j.status === 'claimed').length;

  const requeue = async (j: BuildJob & { id: string }): Promise<void> => {
    setActionErr(null);
    setBusy(j.id);
    try {
      await services.callable.adminRequeueBuild({ creationId: j.creationId, jobId: j.id });
    } catch (e) {
      setActionErr(e instanceof Error ? e.message : 'Requeue failed.');
    } finally {
      setBusy(null);
    }
  };
  const forceFail = async (j: BuildJob & { id: string }): Promise<void> => {
    if (!window.confirm(`Force-fail job ${shortId(j.id, 8)}? The worker's result will be discarded.`)) return;
    setActionErr(null);
    setBusy(j.id);
    try {
      await services.callable.adminFailBuild({
        creationId: j.creationId,
        jobId: j.id,
        reason: 'failed by admin',
      });
    } catch (e) {
      setActionErr(e instanceof Error ? e.message : 'Fail failed.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="panel">
      <h2 className="panel__title">
        Build queue ({inFlight} in flight)
        <span className="muted"> · newest {jobs.length} jobs</span>
      </h2>
      {error && (
        <p className="muted" style={{ color: 'var(--danger, #f87171)' }}>
          Could not load the build queue: {error}
        </p>
      )}
      {actionErr && <div className="error-text">{actionErr}</div>}
      <div className="table__scroll">
        <table className="table">
          <thead>
            <tr>
              <th>Job</th>
              <th>Creation</th>
              <th>Version</th>
              <th>Status</th>
              <th>Worker</th>
              <th>Age</th>
              <th>Error</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {jobs.map((j) => {
              const done = j.status === 'done';
              const terminal = done || j.status === 'failed';
              // Claimed jobs age from their last heartbeat (staleness); others
              // from creation.
              const ageMs =
                j.status === 'claimed' && j.heartbeatAt ? now - j.heartbeatAt : now - (j.createdAt ?? now);
              const ageLabel =
                j.status === 'claimed' && j.heartbeatAt ? `${fmtAge(ageMs)} since ♥` : `${fmtAge(ageMs)} old`;
              return (
                <tr key={j.id}>
                  <td>{shortId(j.id, 10)}</td>
                  <td>{shortId(j.creationId, 10)}</td>
                  <td>{j.version != null ? `v${j.version}` : '—'}</td>
                  <td>
                    {j.status}
                    {(j.attempts ?? 0) > 0 && <span className="muted"> ·{j.attempts}⟳</span>}
                  </td>
                  <td>{j.workerId ?? '—'}</td>
                  <td className="muted">{ageLabel}</td>
                  <td className="muted" style={{ maxWidth: 240 }}>{j.error ?? '—'}</td>
                  <td>
                    <div className="row">
                      <button
                        className="btn btn--ghost btn--sm"
                        onClick={() => void requeue(j)}
                        disabled={busy !== null || done}
                        data-testid={`requeue-${j.id}`}
                      >
                        {busy === j.id ? '…' : 'Requeue'}
                      </button>
                      <button
                        className="btn btn--danger btn--sm"
                        onClick={() => void forceFail(j)}
                        disabled={busy !== null || terminal}
                        data-testid={`fail-${j.id}`}
                      >
                        Fail
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function Admin() {
  return (
    <div className="container" style={{ paddingBottom: 48 }}>
      <div className="gallery__hero gallery__hero--row">
        <div>
          <h1 className="gallery__title">Admin</h1>
          <p className="gallery__subtitle">
            Platform controls — superadmin only. <Link to="/admin/stats">Admin Stats →</Link>
          </p>
        </div>
        <Mascot context="admin" size={110} quip={false} />
      </div>
      <SettingsForm />
      <QualityV2Panel />
      <BuildPipelinesPanel />
      <EditorsPickPanel />
      <RoleManager />
      <AllCreations />
      <BuildQueue />
      <FailedCandidateInspectionPanel />
      <LlmMonitor />
      <ActionLog />
    </div>
  );
}
